import { computed, inject, ref, watch } from 'vue';
import { DetailItemInfo } from '@/common/components/organisms/alertListDetail/alertListDetail.types';
import { AlertType } from '@/alert/components/alertDetail/alertDetail.types';
import type { EventAlertDetailInfo } from '@/alert/utils/types';
import { buildEventAlertDetailInfoFromDetailItem } from '@/alert/utils/eventAlertDetailOpen.utils';
import { useInternational } from '@/common/locale';
import { useCriteriaTooltip } from '@/alert/components/alertGrid/alertGrid.setup';
import { getAlertTarget } from '@/alert/utils/utils';
import { SYSTEM_ALERT_GROUP_ID } from './alertListDetail.define';

export interface Props {
  selectedId: string;
  selectedGroupId: string;
  list: DetailItemInfo[];
  title?: string;
}
export interface Emit {
  (e: 'update:selected-id', value: string): void;
  (e: 'click-item', value: DetailItemInfo | null): void;
  (
    e: 'clear-items',
    value: {
      items: DetailItemInfo[];
      type: AlertType;
    },
  ): void;
  (e: 'openDetail'): void;
  (e: 'reFetch'): void;
}

export const setup = (props: Props, emit: Emit) => {
  const { t } = useInternational();

  const isInitAnimation = ref<boolean>(true);

  const selectedId = computed<string>({
    get: () => props.selectedId,
    set: (val: string) => emit('update:selected-id', val),
  });

  const selectedItem = computed<DetailItemInfo | undefined>(() => {
    return props.list.find((item) => item.cardId === selectedId.value);
  });

  const eventAlertInfo = computed<EventAlertDetailInfo | undefined>(() => {
    const select = selectedItem.value;
    if (!select) return undefined;
    return buildEventAlertDetailInfoFromDetailItem(select) ?? undefined;
  });

  const alertDetailTitle = computed(() => {
    const select = selectedItem.value;
    if (!select) return '';

    const { alertTargetId } = getAlertTarget(select.ruleCriteria, select.targetList, select.type);
    let targetName = '';
    if (alertTargetId) {
      const alertTarget = select.targetList?.find((target) => target.id === alertTargetId);
      if (alertTarget) {
        targetName = (alertTarget.alias || alertTarget.name) ?? '';
      }
    }
    return `${t('WORD.ALERT')}: ${select.name}${targetName ? ` (${targetName})` : ''}`;
  });

  const onClickItem = (item: DetailItemInfo) => {
    if (selectedId.value === item.cardId || !item.name || !item.type) {
      selectedId.value = '';
      emit('click-item', null);
      return;
    }

    if (!buildEventAlertDetailInfoFromDetailItem(item)) {
      selectedId.value = '';
      emit('click-item', null);
      return;
    }

    selectedId.value = item.cardId;
    emit('click-item', item);
  };
  const onClickClear = (item: DetailItemInfo) => {
    const items = item.id && item.name ? [item] : props.list;
    const type = props.selectedGroupId === SYSTEM_ALERT_GROUP_ID ? 'system' : 'user';
    emit('clear-items', { items, type });
  };

  const onClickCloseDetail = () => {
    selectedId.value = '';
    emit('click-item', null);
  };

  const emitOpenDetail = inject('emitOpenDetail');
  const onDetail = () => {
    if (typeof emitOpenDetail === 'function') {
      emitOpenDetail();
    }
  };

  const { criteriaTooltipMouseEvent, criteriaTooltipText, onCriteriaMouse } = useCriteriaTooltip();

  let animationTimer: ReturnType<typeof setTimeout> | null = null;

  watch(
    () => props.selectedGroupId,
    () => {
      isInitAnimation.value = false;

      if (animationTimer) {
        clearTimeout(animationTimer);
        animationTimer = null;
      }

      requestAnimationFrame(() => {
        isInitAnimation.value = true;

        animationTimer = setTimeout(() => {
          isInitAnimation.value = false;
          animationTimer = null;
        }, 1000);
      });
    },
  );

  return {
    selectedId,
    eventAlertInfo,
    alertDetailTitle,
    onClickItem,
    onClickClear,
    onClickCloseDetail,
    onDetail,
    t,
    isInitAnimation,

    criteriaTooltipMouseEvent,
    criteriaTooltipText,
    onCriteriaMouse,
  };
};
